import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import dashboardService from '../../services/dashboardService';
import { FiUsers, FiCheckSquare, FiClock, FiTrendingUp, FiMail, FiAward, FiEdit, FiTrash2 } from 'react-icons/fi'; import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import taskService from '../../services/taskService';
import departmentService from '../../services/departmentService';
import { customDateFormatShort } from '../../config/utils';
import roleService from '../../services/roleService';
import { FaFileExcel } from 'react-icons/fa';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


const formatLastLogin = (dateString) => {
  const date = new Date(dateString);

  const day = date.getDate();

  const weekday = date.toLocaleString("en-IN", { weekday: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${weekday}, ${day} ${year}, ${time}`;
};


const roleOptions = [
  { value: "Manager", label: "Manager" },
  { value: "Admin", label: "Admin" },
  { value: "Member", label: "Member" },
];


const TeamDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showPopup, setShowPopup] = useState(false);
  const { registerByAdmin, register } = useAuth();
  const [editingMember, setEditingMember] = useState(null);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    first_name: '',
    last_name: '',
    role_name: '',
    reports_to: '',
    department_id: '',
  });

  const handleChange = (e) => {
    console.log("forms", e)
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    loadData();
    loadUsers();
    loadDepartments();
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAllRoles();
      console.log("response ===", response)
      setRoles(response.data || []);
    } catch (error) {
      setError('Failed to fetch roles');
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // This would need a user service - for now using mock data
      const data = await taskService.getAll();
      console.log("users", data)
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await departmentService.getAllDepartments();
      setDepartments(data.departments || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await dashboardService.getTeamDashboard();
      setData(result);
    } catch (error) {
      console.error('Error loading team dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortedMembers = () => {
    if (!data?.members) return [];

    const sorted = [...data.members].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const handleDownloadTeamList = () => {
    const members = getSortedMembers();
    if (members.length === 0) {
      toast.error('No team members to download');
      return;
    }

    const headers = [
      'Name', 'Email', 'Completed Tasks', 'Assigned Tasks', 'Completion %',
      'Total Hours', 'Billable Hours', 'Utilization %', 'Status', 'Last Login'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = members.map((member) => [
      member.name,
      member.email,
      member.completedTasks,
      member.assignedTasks,
      member.completionRate,
      member.totalHours,
      member.billableHours,
      member.utilization,
      member.is_active,
      member.last_login ? formatLastLogin(member.last_login) : ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `team-list-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Team list downloaded');
  };

  const handleEdit = (member) => {
    console.log("member===",member)
    setFormData({
      email: member.email,
      mobile: member.mobile || '',
      first_name: member.name.split(' ')[0],
      last_name: member.name.split(' ')[1] || '',
      role_name: member.role.name || '',
      department_id: member.department_id || '',
    });
    setEditingMember(member);
    setShowPopup(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await dashboardService.deleteTeamMember(memberId);
      toast.success('Member removed.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editingMember) {
        await dashboardService.updateTeamMember(editingMember.id, formData);
        toast.success('Member updated successfully!');
      } else {
        await dashboardService.createTeamMemberByAdmin(formData);
        toast.success('Created successful!');
      }
      loadData();
      setShowPopup(false);
      setEditingMember(null);
      setFormData({ email: '', mobile: '', first_name: '', last_name: '', role_name: '', department_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading team dashboard...</div>
      </div>
    );
  }

  const utilizationData = {
    labels: data?.members?.map(m => m.name.split(' ')[0]) || [],
    datasets: [{
      label: 'Utilization %',
      data: data?.members?.map(m => m.utilization) || [],
      backgroundColor: data?.members?.map(m => {
        if (m.utilization >= 80) return '#10B981';
        if (m.utilization >= 60) return '#3B82F6';
        if (m.utilization >= 40) return '#F59E0B';
        return '#EF4444';
      }) || [],
      borderRadius: 8,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y}% utilized`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 80) return 'text-green-600';
    if (utilization >= 60) return 'text-blue-600';
    if (utilization >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompletionColor = (rate) => {
    if (rate >= 80) return 'badge-success';
    if (rate >= 60) return 'badge-primary';
    if (rate >= 40) return 'badge-warning';
    return 'badge-danger';
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Dashboard</h1>
          <p className="text-gray-600 mt-1">Team performance and productivity metrics</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Team Members</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.summary?.totalMembers || 0}</p>
            </div>
            <FiUsers className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Tasks</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.summary?.totalTasks || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{data?.summary?.completedTasks || 0} completed</p>
            </div>
            <FiCheckSquare className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Total Hours</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.summary?.totalHours?.toFixed(1) || 0}h</p>
            </div>
            <FiClock className="w-12 h-12 text-purple-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Avg Utilization</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{data?.summary?.averageUtilization || 0}%</p>
            </div>
            <FiTrendingUp className="w-12 h-12 text-orange-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Utilization Chart */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Utilization</h2>
        <div className="h-80">
          <Bar data={utilizationData} options={chartOptions} />
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 d-flex justify-between">
          <div><h2 className="text-xl font-semibold text-gray-900">Team Member Details</h2></div>
          <div className="flex gap-2">
            <button className='btn btn-primary' onClick={() => setShowPopup(true)}>Add Member</button>
            <button
              onClick={handleDownloadTeamList}
              className="px-4 py-3 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
              title="Download team list"
            >
              <FaFileExcel />
            </button>
          </div>
        </div>
        {showPopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 ">
            <div className="bg-white p-6 rounded-lg shadow-lg" style={{ width: '700px' }}>
              <h3 className="text-lg font-semibold mb-4 ">Add Member</h3>

              <div className="settings-section">
                <form className="login-form" onSubmit={handleSubmit}>

                  <div className="settings-form mb-6">
                    <div className="flex gap-4 -mx-2">
                      <div className=" w-1/2 px-2">
                        <div className="form-group">
                          <label>First Name</label>
                          <input
                            type="text"
                            name="first_name"
                            placeholder="John"
                            value={formData.first_name}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="w-1/2 px-2">
                        <div className="form-group ">
                          <label>Last Name</label>
                          <input
                            type="text"
                            placeholder="Doe"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className=" flex gap-4 -mx-2">
                      <div className="w-1/2 px-2">
                        <div className="form-group col-md-12">
                          <label>Email</label>
                          <input
                            type="email"
                            placeholder="johndoe@example.com"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="w-1/2 px-2">
                        <div className="form-group col-md-12">
                          <label>Mobile</label>
                          <input
                            type="tel"
                            value={formData.mobile}
                            name="mobile"
                            onChange={handleChange}
                            placeholder="912345678"
                          />
                        </div>
                      </div>

                    </div>

                    <div className="flex gap-4 -mx-2">
                      <div className="w-1/2 px-2">
                        <div className="form-group col-md-12">
                          <label htmlFor="priority">Role</label>
                          <select
                            id="role"
                            name="role_name"
                            value={formData.role_name}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select</option>
                            {roles.map(role => (
                              <option key={role.id} value={role.name}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="w-1/2 px-2">
                        <div className="form-group ">
                          <label htmlFor="reports_to">Reports To</label>
                          <select
                            id="reports_to"
                            name="reports_to"
                            value={formData.reports_to}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Select assignee"
                          >
                            <option value="">Select</option>
                            {users.map(user => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="w-1/2 px-2">
                        <div className="form-group ">
                          <label htmlFor="department_id">Department</label>
                          <select
                            id="department_id"
                            name="department_id"
                            value={formData.department_id}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Select department"
                          >
                            <option value="">Select</option>
                            {departments.map(department => (
                              <option key={department.id} value={department.id}>
                                {department.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>


                    </div>
                  </div>
                </form>
              </div>

              <div className='d-flex justify-end'>


                <button
                  className="btn btn-secondary"
                  onClick={() => { setShowPopup(false); setEditingMember(null); }}
                >
                  Close
                </button>

                <button className="btn btn-primary" onClick={() => handleSubmit()} disabled={loading}>
                  {loading ? 'Adding...' : 'Add'}
                </button>



              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Team Member</span>
                    {sortBy === 'name' && <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('assignedTasks')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Tasks</span>
                    {sortBy === 'assignedTasks' && <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('completionRate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Completion</span>
                    {sortBy === 'completionRate' && <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalHours')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Hours</span>
                    {sortBy === 'totalHours' && <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('utilization')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Utilization</span>
                    {sortBy === 'utilization' && <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedMembers().length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No team members found
                  </td>
                </tr>
              ) : (
                getSortedMembers().map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {member.avatar ? (
                            <img
                              src={member.avatar}
                              alt={member.name}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                              {member.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{member.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <FiMail className="w-4 h-4 mr-2" />
                        {member.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {member.completedTasks}/{member.assignedTasks}
                      </div>
                      <div className="text-xs text-gray-500">assigned</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-center ${getCompletionColor(member.completionRate)}`}>
                        {member.completionRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.totalHours}h</div>
                      <div className="text-xs text-gray-500">{member.billableHours}h billable</div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${member.utilization >= 80 ? 'bg-green-600' :
                              member.utilization >= 60 ? 'bg-blue-600' :
                                member.utilization >= 40 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                            style={{ width: `${member.utilization}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${getUtilizationColor(member.utilization)}`}>
                          {member.utilization}%
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {member.is_active}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {/* {
                          member.last_login &&
                          new Date(member.last_login).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        } */}
                        {member.last_login && formatLastLogin(member.last_login)}

                      </div>

                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(member)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit member"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete member"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <FiAward className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">High Performers</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">
            {data?.members?.filter(m => m.utilization >= 80 && m.completionRate >= 80).length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-2">Members exceeding expectations</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <FiCheckSquare className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">On Track</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {data?.members?.filter(m => m.utilization >= 60 && m.utilization < 80).length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-2">Members performing well</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <FiTrendingUp className="w-6 h-6 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900">Needs Support</h3>
          </div>
          <p className="text-3xl font-bold text-yellow-600">
            {data?.members?.filter(m => m.utilization < 60).length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-2">Members needing attention</p>
        </div>
      </div>
    </div>
  );
};

export default TeamDashboard;

